import { Injectable } from '@angular/core';
import { Project } from '../../models';
import { Column, Field, FieldBreadcrumb, Section } from './models';

@Injectable({
    providedIn: 'root'
})
export class BreadcrumbLogicService {

    constructor() { }

    createBreadcrumbs(project: Project, projects: Project[] = []): void {
        const fieldBreadcrumbs = this.getFieldBreadcrumbs(project.JSONForms?.Sections);

        // Summary project fields are copies of source project fields, so they use the source project's name and sections.
        const sourceBreadcrumbsByFieldKey = project.isSummaryProject
            ? this.collectSourceBreadcrumbs(fieldBreadcrumbs, projects)
            : new Map<string, FieldBreadcrumb>();

        for (const fieldBreadcrumb of fieldBreadcrumbs) {
            const field = fieldBreadcrumb.field;
            const sourceBreadcrumb = field.projectId ? sourceBreadcrumbsByFieldKey.get(this.getFieldKey(field.projectId, field.id)) : undefined;
            field.breadcrumb = sourceBreadcrumb?.breadcrumb ?? fieldBreadcrumb.breadcrumb;
            field.parent = sourceBreadcrumb?.parent ?? fieldBreadcrumb.parent;
        }
    }

    private buildFieldBreadcrumb(field: Field, crumbs: string[]): FieldBreadcrumb {
        return {
            field: field,
            breadcrumb: `${crumbs.join(' > ')} > `,
            parent: `... > ${crumbs[crumbs.length - 1]}`
        };
    }

    private collectSourceBreadcrumbs(summaryFieldBreadcrumbs: FieldBreadcrumb[], projects: Project[]): Map<string, FieldBreadcrumb> {
        const referencedProjectIds = new Set<string>();
        for (const fieldBreadcrumb of summaryFieldBreadcrumbs) {
            if (fieldBreadcrumb.field.projectId) {
                referencedProjectIds.add(fieldBreadcrumb.field.projectId);
            }
        }

        const sourceBreadcrumbsByFieldKey = new Map<string, FieldBreadcrumb>();
        const collectedProjectIds = new Set<string>();

        for (const sourceProject of projects) {
            if (!sourceProject.id || sourceProject.isSummaryProject || !referencedProjectIds.has(sourceProject.id) || collectedProjectIds.has(sourceProject.id)) {
                continue;
            }

            collectedProjectIds.add(sourceProject.id);
            const projectPrefix = sourceProject.name ? `${sourceProject.name} > ` : '';

            for (const fieldBreadcrumb of this.getFieldBreadcrumbs(sourceProject.JSONForms?.Sections)) {
                sourceBreadcrumbsByFieldKey.set(this.getFieldKey(sourceProject.id, fieldBreadcrumb.field.id), {
                    field: fieldBreadcrumb.field,
                    breadcrumb: `${projectPrefix}${fieldBreadcrumb.breadcrumb}`,
                    parent: fieldBreadcrumb.parent
                });
            }
        }

        return sourceBreadcrumbsByFieldKey;
    }

    private getColumnCrumbs(column: Column, sectionCrumbs: string[]): string[] {
        if (column.type !== 'group') {
            return sectionCrumbs;
        }

        return [...sectionCrumbs, column.GroupTitle ? column.GroupTitle : 'Unnamed Column'];
    }

    private getFieldBreadcrumbs(sections: Section[] | null | undefined, parentCrumbs: string[] = []): FieldBreadcrumb[] {
        const fieldBreadcrumbs: FieldBreadcrumb[] = [];

        for (const section of sections ?? []) {
            const sectionCrumbs = [...parentCrumbs, section.SectionTitle ? section.SectionTitle : 'Unnamed Section'];

            for (const column of section.Columns ?? []) {
                const columnCrumbs = this.getColumnCrumbs(column, sectionCrumbs);

                for (const field of column.Fields ?? []) {
                    fieldBreadcrumbs.push(this.buildFieldBreadcrumb(field, columnCrumbs));
                }
            }

            fieldBreadcrumbs.push(...this.getFieldBreadcrumbs(section.Sections, sectionCrumbs));
        }

        return fieldBreadcrumbs;
    }

    private getFieldKey(projectId: string, fieldId: string): string {
        return `${projectId}:${fieldId}`;
    }
}
